/**
 * CLARIAS API Service - Centralized API Configuration
 * 
 * BULLETPROOF DESIGN:
 * - In PRODUCTION: Always uses relative URL '/api' 
 *   (browser automatically prepends current domain)
 * - In DEVELOPMENT: Uses REACT_APP_BACKEND_URL for preview testing
 * 
 * This ensures the frontend NEVER breaks due to URL misconfiguration!
 */

// Runtime check - happens in browser, not at build time
const getIsDevelopment = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

// API_BASE: The base URL for all API calls
// - Production (clarias.in): '/api' → browser uses https://clarias.in/api
// - Development (localhost): Uses env var or preview URL
export const API_BASE = getIsDevelopment()
  ? (process.env.REACT_APP_BACKEND_URL || '') + '/api'
  : '/api';

/**
 * Get the full API URL for an endpoint
 * Prevents double /api prefix
 */
export const getApiUrl = (endpoint) => {
  // Clean the endpoint
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : '/' + endpoint;
  
  // If already has /api prefix, handle it
  if (cleanEndpoint.startsWith('/api/') || cleanEndpoint === '/api') {
    // In production, use as-is (relative)
    // In development, prepend the backend URL
    if (getIsDevelopment()) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      return backendUrl + cleanEndpoint;
    }
    return cleanEndpoint;
  }
  
  // Normal case: prepend API_BASE
  return API_BASE + cleanEndpoint;
};

/**
 * Helper to make API calls with proper error handling
 */
export const apiCall = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });
  return response;
};

/**
 * Helper for authenticated API calls
 */
export const authApiCall = async (endpoint, token, options = {}) => {
  return apiCall(endpoint, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    },
  });
};

// Log API_BASE for debugging (only in development)
if (getIsDevelopment()) {
  console.log('[API Service] Development mode - API_BASE:', API_BASE);
}

export default {
  API_BASE,
  getApiUrl,
  apiCall,
  authApiCall,
};

// Production-aware API URL
// In production: returns empty string (uses relative URLs)
// In development: returns REACT_APP_BACKEND_URL
export const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return ''; // Production - use relative URLs
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
